import { Injectable } from '@angular/core';

export interface ParsedError {
  field?: string;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class ErrorParserService {
  /**
   * Parses backend error responses into a consistent format
   * Handles multiple error structures:
   * 1. Array of strings: ["Error message 1", "Error message 2"]
   * 2. Object with field keys: { field1: ["Error 1"], field2: ["Error 2"] }
   * 3. Array of objects: [{ field: "field1", message: "Error 1" }]
   * 4. Single string: "Error message"
   * 5. Object with error key: { error: "Error message" } or { error: { message: "Error" } }
   */
  parseError(error: any): ParsedError[] {
    const parsedErrors: ParsedError[] = [];

    if (!error) {
      return parsedErrors;
    }

    // If error has an 'error' property (common in HTTP responses)
    const errorData = error.error || error;

    this._extractErrors(errorData, parsedErrors);

    // Fallback: if no errors were parsed, return a generic message
    if (parsedErrors.length === 0) {
      parsedErrors.push({ message: 'An unexpected error occurred. Please try again.' });
    }

    return parsedErrors;
  }

  private _extractErrors(errorData: any, parsedErrors: ParsedError[], parentField: string = ''): void {
    if (!errorData) return;

    // Case 1: Array of strings or objects
    if (Array.isArray(errorData)) {
      errorData.forEach((err: any) => {
        if (typeof err === 'string') {
          parsedErrors.push({ field: parentField, message: err });
        } else if (err && typeof err === 'object') {
          if (err.field && err.message) {
            // If it already has a field, prefix it if needed, otherwise use it
            const fullField = parentField ? `${parentField}.${err.field}` : err.field;
            parsedErrors.push({ field: fullField, message: err.message });
          } else if (err.message) {
            parsedErrors.push({ field: parentField, message: err.message });
          } else {
            // Recurse for array of random objects
            this._extractErrors(err, parsedErrors, parentField);
          }
        }
      });
      return;
    }

    // Case 2: Single string
    if (typeof errorData === 'string') {
      parsedErrors.push({ field: parentField, message: errorData });
      return;
    }

    // Case 3: Object with various structures
    if (typeof errorData === 'object') {
      // Check for common error message keys (non-nested)
      if (errorData.message && typeof errorData.message === 'string') {
        parsedErrors.push({ field: parentField, message: errorData.message });
        return;
      }

      if (errorData.detail && typeof errorData.detail === 'string') {
        parsedErrors.push({ field: parentField, message: errorData.detail });
        return;
      }

      // Iterate through keys
      Object.keys(errorData).forEach(key => {
        const value = errorData[key];

        // Skip non-error fields
        if (['status', 'statusText', 'ok', 'headers', 'url', 'name'].includes(key)) {
          return;
        }

        const currentField = parentField ? `${parentField}.${key}` : key;
        this._extractErrors(value, parsedErrors, currentField);
      });
    }
  }

  /**
   * Formats parsed errors into a single string for display
   */
  formatErrorsAsString(errors: ParsedError[]): string {
    return errors
      .map(err => {
        if (err.field) {
          return `${this.formatFieldName(err.field)}: ${err.message}`;
        }
        return err.message;
      })
      .join('\n');
  }

  /**
   * Formats field name from snake_case to Title Case
   */
  private formatFieldName(field: string): string {
    return field.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
  }

  /**
   * Gets error message for a specific field
   */
  getFieldError(errors: ParsedError[], fieldName: string): string | null {
    const fieldError = errors.find(err => err.field === fieldName);
    return fieldError ? fieldError.message : null;
  }

  /**
   * Gets all non-field errors (general errors)
   */
  getGeneralErrors(errors: ParsedError[]): ParsedError[] {
    return errors.filter(err => !err.field);
  }
}
